import React, { PropsWithChildren } from 'react'
import { Link as MedkitLink, useModalActions } from '@babylon/medkit'
import { Link } from '@babylon/web-platform-ui'
import { useFormatMessage } from '@babylon/intl'
import { Link as ReactRouterLink } from 'react-router-dom'
import { Image } from '@/components'
import { Logo as LogoType } from '@/types'
import styles from './Header.module.scss'
import { useConfig } from '@/redux/partnerConfig/selectors'
import { URL_PREFIX } from '@/constants'
import { useAuth0Wrapper } from '@/hooks'
import messages from '../Logout/Banner/messages'
import LogOutModal from '../LogOutModal/LogOutModal'

export interface Props {
  logos?: LogoType[]
}

interface LogoProps {
  logo: LogoType
  url: string
  useDnaV2Design?: Boolean
}

const Logo = ({ logo, url, useDnaV2Design }: LogoProps) => {
  if (useDnaV2Design) {
    return (
      <Link
        as={ReactRouterLink}
        to={`${url}/logout`}
        data-testid={`headerLogo-${logo.src}`}
        replace
      >
        <Image
          className={styles.header__image}
          src={logo.src}
          alt={logo.alt}
          data-testid={`headerLogoImage-${logo.src}`}
        />
      </Link>
    )
  }

  return (
    <MedkitLink
      className={styles.header__logo}
      to={url}
      data-testid={`headerLogo-${logo.src}`}
    >
      <Image
        className={styles.header__image}
        src={logo.src}
        alt={logo.alt}
        data-testid={`headerLogoImage-${logo.src}`}
      />
    </MedkitLink>
  )
}

const Header = ({ logos = [], children }: PropsWithChildren<Props>) => {
  const { partnerUrlSlug, useDnaV2Design } = useConfig()
  const translate = useFormatMessage()
  const { isAuthenticated } = useAuth0Wrapper()
  const { openModal } = useModalActions()
  const rootUrl = `/${URL_PREFIX}/${partnerUrlSlug}`
  const isWelcomePage = window.location.pathname.includes('welcome')

  return (
    <header className={styles.header} data-testid="Header">
      <div className={styles.header__content}>
        <div className={styles.header__logosWrapper}>
          {logos.map((logo) => (
            <Logo
              logo={logo}
              url={rootUrl}
              key={logo.src}
              useDnaV2Design={Boolean(useDnaV2Design)}
            />
          ))}
        </div>
        <div className={styles.header__rightContent}>
          {children}
          {isAuthenticated && !isWelcomePage && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link
              className={styles.header__rightContent__logoutLink}
              as="button"
              type="button"
              onClick={() => openModal(<LogOutModal />)}
              data-testid="logoutModalButton"
            >
              {translate(messages.button)}
            </Link>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
